/* ==========================================================================
   Session state.

   Holds the current authenticated user + token in memory, persisted to
   localStorage so a refresh doesn't log the user out. This is a frontend
   convenience only — it is NOT the access-control boundary. Every
   permission check here (`can`) controls what the UI *shows*; the backend
   must independently re-check the same permission on every request, since
   a modified frontend or a direct API call must not be able to bypass it.
   ========================================================================== */

import { config } from "../config";
import type { AuthUser, Permission, Session, UserRole } from "../types";

type Listener = (session: Session | null) => void;

const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  user: [
    "wallet:view",
    "wallet:fund",
    "electricity:buy",
    "bills:pay",
  ],
  estate_admin: [
    "wallet:view",
    "wallet:fund",
    "electricity:buy",
    "bills:pay",
    "bills:create",
    "meter:link",
    "meter:unlink",
    "estate:manage_users",
  ],
  // Owners of the business. Has everything estate_admin has for their own
  // estate, plus the one thing estate_admin deliberately does NOT have:
  // the power to change anyone's role, in any estate. That's intentional
  // per the business rule this was built against — estate admins are
  // promoted only by a super_admin, never by each other or themselves.
  super_admin: [
    "wallet:view",
    "wallet:fund",
    "electricity:buy",
    "bills:pay",
    "bills:create",
    "meter:link",
    "meter:unlink",
    "estate:manage_users",
    "admin:promote_users",
  ],
};

let currentSession: Session | null = null;
const listeners = new Set<Listener>();

function loadFromStorage(): Session | null {
  try {
    const raw = window.localStorage.getItem(config.storageKeys.session);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (!parsed || !parsed.token || !parsed.user) return null;
    return parsed;
  } catch {
    // Corrupt or inaccessible storage — treat as logged out rather than throw.
    return null;
  }
}

function persist(session: Session | null): void {
  try {
    if (session) {
      window.localStorage.setItem(config.storageKeys.session, JSON.stringify(session));
    } else {
      window.localStorage.removeItem(config.storageKeys.session);
    }
  } catch {
    // Storage may be unavailable (private browsing, quota). Session still
    // works for the current tab load via the in-memory copy.
  }
}

function notify(): void {
  listeners.forEach((fn) => fn(currentSession));
}

currentSession = loadFromStorage();

export const sessionStore = {
  get current(): Session | null {
    return currentSession;
  },

  get user(): AuthUser | null {
    return currentSession?.user ?? null;
  },

  get isAuthenticated(): boolean {
    return currentSession !== null;
  },

  set(session: Session): void {
    currentSession = session;
    persist(session);
    notify();
  },

  clear(): void {
    currentSession = null;
    persist(null);
    notify();
  },

  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

/** Central permission check. Every place in the UI that decides whether to
 *  show a control gated by role (most importantly: wallet withdrawal)
 *  should call this rather than comparing `role` directly, so the mapping
 *  lives in exactly one place. */
export function can(permission: Permission): boolean {
  const role = currentSession?.user.role;
  if (!role) return false;
  return ROLE_PERMISSIONS[role].includes(permission);
}
